import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { makeStyles } from '../tools/makeStyles';
import { withStyles } from 'tss-react/mui';
import Button from '@mui/material/Button';
import Badge from '@mui/material/Badge';
import EmojiObjectsIcon from '@mui/icons-material/EmojiObjects';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt';
import ReplyIcon from '@mui/icons-material/Reply';

const BADGE_COLOR = 'primary';

const StyledBadge = withStyles(Badge, () => ({
  root: {
    width: '100%',
    justifyContent: 'inherit',
  },
  badge: {
    transform: 'scale(1) translate(8px, -8px)',
    padding: '0 4px',
  },
}));

const useStyles = makeStyles((theme) => ({
  iconContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    boxSizing: 'border-box',
    flexDirection: 'row',
  },
  iconButton: {
    padding: 8,
    borderTop: '1px solid #ccc',
    borderRadius: '0px',
    borderBottom: '1px solid #ccc',
    borderRight: '1px solid #ccc',
    '&:first-child': {
      borderLeft: '1px solid #ccc',
      borderRadius: '4px 0px 0px 4px',
    },
    '&:last-child': {
      borderRadius: '0px 4px 4px 0px',
    },
  },
  activeIcon: {
    background: '#14A5B2',
    color: theme.palette.common.white,
  },
  reply: {
    backgroundColor: '#e88126',
    color: theme.palette.common.white,
  },
}));

/**
 * ReactionButtons
 *
 * Reaction buttons to show what the user think about the employee message.
 * When in read mode, this can also be used to show the employee what their managers (users) thought about their message.
 *
 * @param onClickLike | function | f => f | Callback for when the like button is clicked |
 * @param reactedToLike | boolean | false | Active state of the like button |
 * @param likeCount | number | 0 | Number of reactions of the like button |
 * @param onClickGreatIdea | function | f => f | Callback for when the greatIdea button is clicked |
 * @param reactedToGreatIdea | boolean | false | Active state of the greatIdea button |
 * @param greatIdeaCount | number | 0 | Number of reactions of the greatIdea button |
 * @param onClickWorkingOnIt | function | f => f | Callback for when the workingOnIt button is clicked |
 * @param reactedToWorkingOnIt | boolean | false | Active state of the workingOnIt button |
 * @param workingOnItCount | number | 0 | Number of reactions of the workingOnIt button |
 * @param onClickReply | function | f => f | Callback for when the reply button is clicked |
 * @param hasReply | boolean | false | Display state of the reply button |
 */
const ReactionButtons = ({
  onClickLike = (f) => f,
  reactedToLike = false,
  likeCount = 0,
  onClickGreatIdea = (f) => f,
  reactedToGreatIdea = false,
  greatIdeaCount = 0,
  onClickWorkingOnIt = (f) => f,
  reactedToWorkingOnIt = false,
  workingOnItCount = 0,
  onClickReply = (f) => f,
  hasReply = false,
  readOnly = false,
}) => {
  ReactionButtons.propTypes = {
    /**
     * Function to be triggered after clicking at the like button
     */
    onClickLike: PropTypes.func,
    /**
     * Shows if the user has reacted to the like button or not
     */
    reactedToLike: PropTypes.bool,
    /**
     * The number of reactions it has received
     */
    likeCount: PropTypes.number,
    /**
     * Function to be triggered after clicking at the great idea button
     */
    onClickGreatIdea: PropTypes.func,
    /**
     * Shows if the user has reacted to the great idea button or not
     */
    reactedToGreatIdea: PropTypes.bool,
    /**
     * The number of reactions it has received
     */
    greatIdeaCount: PropTypes.number,
    /**
     * Function to be triggered after clicking at the working on it button
     */
    onClickWorkingOnIt: PropTypes.func,
    /**
     * Shows if the user has reacted to the working on it button or not
     */
    reactedToWorkingOnIt: PropTypes.bool,
    /**
     * The number of reactions it has received
     */
    workingOnItCount: PropTypes.number,
    /**
     * Function to be triggered after clicking at the reply button
     */
    onClickReply: PropTypes.func,
    /**
     * Shows the reply button if `true`
     */
    hasReply: PropTypes.bool.isRequired,
    /**
     * Disables every button if `true`
     */
    readOnly: PropTypes.bool.isRequired,
  };

  const classes = useStyles();

  return (
    <>
      <div className={classes.iconContainer}>
        <Button
          data-testid="chat-great"
          disabled={readOnly}
          onClick={onClickGreatIdea}
          className={classNames(classes.iconButton, {
            [classes.activeIcon]: reactedToGreatIdea,
          })}
        >
          <StyledBadge badgeContent={greatIdeaCount} color={BADGE_COLOR}>
            <EmojiObjectsIcon />
          </StyledBadge>
        </Button>

        <Button
          data-testid="chat-working"
          disabled={readOnly}
          onClick={onClickWorkingOnIt}
          className={classNames(classes.iconButton, {
            [classes.activeIcon]: reactedToWorkingOnIt,
          })}
        >
          <StyledBadge badgeContent={workingOnItCount} color={BADGE_COLOR}>
            <FormatListBulletedIcon />
          </StyledBadge>
        </Button>

        <Button
          data-testid="chat-like"
          disabled={readOnly}
          onClick={onClickLike}
          className={classNames(classes.iconButton, {
            [classes.activeIcon]: reactedToLike,
          })}
        >
          <StyledBadge badgeContent={likeCount} color={BADGE_COLOR}>
            <ThumbUpAltIcon />
          </StyledBadge>
        </Button>

        {hasReply && (
          <Button
            data-testid="chat-reply"
            disabled={readOnly}
            onClick={onClickReply}
            className={classNames(classes.iconButton, classes.reply)}
          >
            <ReplyIcon />
          </Button>
        )}
      </div>
    </>
  );
};

export default ReactionButtons;
